/**
 * 確定済み表示の「確定した根拠記録」カード (AC-DFC-018 / 019、design
 * `demand/forecast-today.html` の `#dft-ro-reason`、#10139)。
 *
 * 5 項目は詰めずに全部並べ、未入力の項目は「—」にする (AC-DFC-018)。
 * `rationale` が無い = 確定済みなのに確定時の記録を読み出せていないので、値も
 * 「—」も出さず案内だけを出す (AC-DFC-019。「書かなかった」と区別する)。
 * 根拠記録は自動の取り直し (AC-DFC-016) の対象外なので再試行はしない。
 *
 * 見出し横の確定日時・確定者も、読み出せない時は記録から読めないものとして出さない。
 *
 * design が dl の先頭に置く「参照した気象観測地点」の行は載せない。確定時の地点を
 * 記録する仕組みが無く、AC-DFC-018 の 5 項目にも含まれないため。
 */
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { DemandConfirmRationale } from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import { useTranslation } from 'react-i18next'

import { Box, Flex, Grid, Text } from '@enechain/ecloud-designsystem'

import { CompactAlert } from '~/components/common/CompactAlert'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { formatDateTimeHyphen, formatDateTimeLocaleJa } from '~/models/dateTime'
import { decimalToNumberOr } from '~/models/decimal'

const FIELDS = [
  'curveRationale',
  'maxPowerRationale',
  'minPowerRationale',
  'numericalBasis',
  'freeNote',
] as const satisfies ReadonlyArray<keyof DemandConfirmRationale>

type Props = {
  /** 確定時の根拠記録。undefined は読み出せなかったことを表す (AC-DFC-019)。 */
  rationale: DemandConfirmRationale | undefined
  confirmedAt: Timestamp
  /** 確定者の eauth member ID。表示名へ解決し、生 ID は出さない (#6538)。 */
  confirmedBy: string | undefined
}

export const ConfirmedRationaleCard = ({
  rationale,
  confirmedAt,
  confirmedBy,
}: Props): React.ReactElement => {
  const { t, i18n } = useTranslation()
  const resolveDisplayName = useMemberDisplayName()
  const confirmedByName =
    confirmedBy !== undefined ? resolveDisplayName(confirmedBy) : undefined
  const confirmedAtLabel = formatDateTimeLocaleJa(timestampDate(confirmedAt))
  const formatAllocatedAt =
    i18n.language === 'en' ? formatDateTimeHyphen : formatDateTimeLocaleJa
  // 帰属不明の振り分けの記録 (AC-DFD-059)。担当者を解決できない時も一文の形を保つため
  // 「—」を入れ、生 ID は出さない (#6538)。
  const allocationLines = (rationale?.unattributedAllocations ?? []).map((a) =>
    t('page:demandForecastDaily.confirmedRationale.unattributedAllocation', {
      amount: Math.round(decimalToNumberOr(a.amountKwh)).toLocaleString(
        'en-US',
      ),
      retailer: a.retailerName,
      user: resolveDisplayName(a.allocatedBy) ?? '—',
      time:
        a.allocatedAt !== undefined
          ? formatAllocatedAt(timestampDate(a.allocatedAt))
          : '—',
    }),
  )
  return (
    <Box
      as="section"
      flexDirection="column"
      gap={2}
      display="flex"
      marginBottom={4}
      background="Surface.primary"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
      aria-labelledby="confirmed-rationale-title"
      paddingX={4}
      paddingY={3}
    >
      <Flex alignItems="baseline" flexWrap="wrap" rowGap={1} columnGap={3}>
        <Text
          as="h3"
          textStyle="sectionTitle"
          color="Text.highEmphasis"
          fontWeight="bold"
          id="confirmed-rationale-title"
        >
          {t('page:demandForecastDaily.confirmedRationale.title')}
        </Text>
        {rationale !== undefined && (
          <Text color="Text.midEmphasis" fontSize="sm">
            {confirmedByName !== undefined
              ? t('page:demandForecastDaily.confirmedRationale.metaWithBy', {
                  time: confirmedAtLabel,
                  user: confirmedByName,
                })
              : t('page:demandForecastDaily.confirmedRationale.meta', {
                  time: confirmedAtLabel,
                })}
          </Text>
        )}
      </Flex>
      {rationale === undefined ? (
        <CompactAlert
          status="warning"
          title={t('page:demandForecastDaily.confirmedRationale.loadError')}
        />
      ) : (
        <Grid
          as="dl"
          gridRowGap="6px"
          gridColumnGap={4}
          gridTemplateColumns="max-content minmax(0, 1fr)"
          margin={0}
          fontSize="md"
        >
          {FIELDS.map((field) => {
            const note = rationale[field]?.trim() ?? ''
            // 自由メモの後ろに振り分けの記録を 1 文ずつ改行で足す。メモが空で記録が
            // ある時は「—」を出さず記録だけにする (AC-DFD-059)。
            const value =
              field === 'freeNote'
                ? [note, ...allocationLines].filter((l) => l !== '').join('\n')
                : note
            return (
              <Box key={field} display="contents">
                <Text as="dt" color="Text.midEmphasis">
                  {t(`page:demandForecastDaily.confirm.${field}`)}
                </Text>
                <Text
                  as="dd"
                  margin={0}
                  color={value === '' ? 'Text.midEmphasis' : undefined}
                  whiteSpace="pre-wrap"
                >
                  {value === '' ? '—' : value}
                </Text>
              </Box>
            )
          })}
        </Grid>
      )}
    </Box>
  )
}
